import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Tabs,
  Tab,
  Box,
  Typography,
  Fade,
  Button,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  People,
  Leaderboard,
  Translate,
  AdminPanelSettings,
  CardGiftcard,
  School,
  Logout,
  PriceChange
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import UserManagement from './UserManagement';
import WordManagement from './WordManagement';
import ScoreManagement from './ScoreManagement';
import ReferralManagement from './ReferralManagement';
import LevelManagement from './LevelManagement';
import PriceSettings from './PriceSettings';
import { resetDatabase } from '../../services/adminService';
import { runMigrations } from '../../services/migration';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../services/firebase';
import { signOut } from 'firebase/auth';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, query, where, doc, updateDoc } from 'firebase/firestore';

const MotionPaper = motion(Paper);

function AdminPanel() {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAdminAuth = async (user) => {
      if (!user) return false;
      
      try {
        console.log('Admin kontrolü yapılıyor:', user.email);
        const adminUsersRef = collection(db, 'adminUsers');
        const q = query(adminUsersRef, where('uid', '==', user.uid));
        const querySnapshot = await getDocs(q);
        
        const isAdmin = !querySnapshot.empty;
        console.log('Admin kontrolü sonucu:', isAdmin);
        return isAdmin;
      } catch (error) {
        console.error('Admin kontrolü hatası:', error);
        return false;
      }
    };

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsLoading(true);
      console.log('Auth durumu değişti:', user?.email);

      if (!user) {
        console.log('Kullanıcı oturumu yok, login sayfasına yönlendiriliyor');
        navigate('/admin/login');
      } else {
        const isAdmin = await checkAdminAuth(user);
        if (!isAdmin) {
          console.log('Admin yetkisi yok, ana sayfaya yönlendiriliyor');
          alert('Bu sayfaya erişim yetkiniz yok');
          navigate('/');
        } else {
          console.log('Admin yetkisi doğrulandı');
          setIsAuthenticated(true);
        }
      }
      setIsLoading(false);
    });

    return () => {
      console.log('Auth listener temizleniyor');
      unsubscribe();
    };
  }, [navigate]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleResetDatabase = async () => {
    if (window.confirm('Veritabanını sıfırlamak istediğinizden emin misiniz? Bu işlem geri alınamaz!')) {
      try {
        await resetDatabase();
        // Başarılı mesajı göster
        alert('Veritabanı başarıyla sıfırlandı');
        // Sayfayı yenile
        window.location.reload();
      } catch (error) {
        // Hata mesajı göster
        alert('Hata: ' + error.message);
      }
    }
  };

  const handleMigrateDatabase = async () => {
    if (!isAuthenticated) {
      alert('Lütfen önce giriş yapın');
      return;
    }

    if (window.confirm('Veritabanı şemasını güncellemek istediğinizden emin misiniz?')) {
      try {
        const result = await runMigrations();
        alert(result.message || 'Veritabanı başarıyla güncellendi');
        window.location.reload();
      } catch (error) {
        console.error('Migration hatası:', error);
        if (error.message.includes('giriş yapın')) {
          navigate('/login');
        } else {
          alert('Hata: ' + (error.message || 'Bilinmeyen bir hata oluştu'));
        }
      }
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Çıkış yapmak istediğinizden emin misiniz?')) {
      try {
        await signOut(auth);
        // Local storage'ı temizle
        localStorage.clear();
        // Session storage'ı temizle
        sessionStorage.clear();
        // Ana sayfaya yönlendir
        navigate('/');
      } catch (error) {
        console.error('Çıkış yapılırken hata:', error);
        alert('Çıkış yapılırken bir hata oluştu');
      }
    }
  };

  const handleResetAllUsers = async () => {
    if (window.confirm('Tüm kullanıcı verilerini sıfırlamak istediğinizden emin misiniz? Bu işlem geri alınamaz!')) {
      try {
        const usersRef = collection(db, 'users');
        const snapshot = await getDocs(usersRef);
        
        // Her kullanıcı için sıfırlama işlemi
        const resetPromises = snapshot.docs.map(async (userDoc) => {
          const userRef = doc(db, 'users', userDoc.id);
          await updateDoc(userRef, {
            score: 0,
            level: 1,
            combo: 0,
            maxCombo: 0,
            correctAnswers: 0,
            updatedAt: new Date()
          });
        });

        await Promise.all(resetPromises);
        alert('Tüm kullanıcı verileri başarıyla sıfırlandı');
        window.location.reload();
      } catch (error) {
        console.error('Kullanıcı verileri sıfırlanırken hata:', error);
        alert('Hata: ' + error.message);
      }
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Fade in timeout={800}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Admin Paneli
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              color="warning"
              onClick={handleMigrateDatabase}
            >
              Veritabanını Güncelle
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleResetDatabase}
            >
              Veritabanını Sıfırla
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleResetAllUsers}
              sx={{
                background: 'linear-gradient(45deg, #FF9800 30%, #FF5722 90%)',
                color: 'white'
              }}
            >
              Kullanıcıları Sıfırla
            </Button>
            <Tooltip title="Çıkış Yap">
              <IconButton 
                onClick={handleLogout}
                sx={{ 
                  ml: 2,
                  background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #FE6B8B 60%, #FF8E53 90%)',
                  }
                }}
              >
                <Logout />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        <MotionPaper
          elevation={3}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          sx={{ 
            p: 3,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'primary.main'
            }}>
              <AdminPanelSettings sx={{ fontSize: 35 }} />
              Admin Paneli
            </Typography>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab 
                icon={<People />} 
                label="Kullanıcı Yönetimi" 
                sx={{ 
                  '&.Mui-selected': {
                    color: 'primary.main',
                  }
                }}
              />
              <Tab 
                icon={<Leaderboard />} 
                label="Puan Durumu" 
                sx={{ 
                  '&.Mui-selected': {
                    color: 'primary.main',
                  }
                }}
              />
              <Tab 
                icon={<Translate />} 
                label="Kelime Yönetimi" 
                sx={{ 
                  '&.Mui-selected': {
                    color: 'primary.main',
                  }
                }}
              />
              <Tab 
                icon={<CardGiftcard />} 
                label="Referans Kodları" 
                sx={{ 
                  '&.Mui-selected': {
                    color: 'primary.main',
                  }
                }}
              />
              <Tab 
                icon={<School />} 
                label="Seviye Yönetimi" 
                sx={{ 
                  '&.Mui-selected': {
                    color: 'primary.main',
                  }
                }}
              />
              <Tab 
                icon={<PriceChange />} 
                label="Paket Fiyatı" 
                sx={{ 
                  '&.Mui-selected': {
                    color: 'primary.main',
                  }
                }}
              />
            </Tabs>
          </Box>

          {currentTab === 0 && <UserManagement />}
          {currentTab === 1 && <ScoreManagement />}
          {currentTab === 2 && <WordManagement />}
          {currentTab === 3 && <ReferralManagement />}
          {currentTab === 4 && <LevelManagement />}
          {currentTab === 5 && <PriceSettings />}
        </MotionPaper>
      </Container>
    </Fade>
  );
}

export default AdminPanel; 